import { addStoreLocaleByCookie } from 'lib/storeLocale/client'
import { useSelector } from 'react-redux'
import { isAuthenticated, getUserID, getIsAdmin } from 'selectors/user/session'
import { AdminUrlLinks } from '../types'
import { RefObject, useEffect, useRef } from 'react'
import { CreateCollectionsModalApi } from 'components/SACollection/components/CreateCollectionsModal'
import useModal from 'hooks/useModal'
import { getHasValidPermissions } from '../helpers/collectionDetailsSelector'

type UseCollectionDetailsActionsParams = {
  adminUrlLinks: AdminUrlLinks
  createCollectionsModalRef: RefObject<CreateCollectionsModalApi>
  openDeleteModal: () => void
}

type GetCreatedCollectionUrlParams = {
  payload: Record<string, any>
  formData: Record<string, any>
  userId: string | number
}

const useCollectionDetailsActions = ({
  adminUrlLinks,
  createCollectionsModalRef,
  openDeleteModal,
}: UseCollectionDetailsActionsParams) => {
  const isAuthenticatedFlag = useSelector(isAuthenticated)
  const userId = useSelector(getUserID)
  const hasValidPermissions = useSelector(getHasValidPermissions)
  const isAdmin = useSelector(getIsAdmin)

  const { showModal } = useModal()
  const createCollectionAfterLogin = useRef(false)

  const handleActionOnClick = (ev, actionUrl) => {
    ev.preventDefault()

    if (!isAuthenticatedFlag) {
      const redirectURL = encodeURI(actionUrl)
      const location = addStoreLocaleByCookie(`/authentication?redirect_url=${redirectURL}`)
      window.location = location
    } else {
      const location = addStoreLocaleByCookie(actionUrl)
      window.location = location
    }
  }

  const getCreatedCollectionUrl = ({
    payload,
    formData,
    userId,
  }: GetCreatedCollectionUrlParams) => {
    const { data, collection_id } = payload
    const { title } = formData
    const isAASelection = !!collection_id
    const transformedTitle = (title ?? '').replace(/\s+/g, '-')
    const newCollectionPath = `${
      isAASelection ? 'aa-collection/' : ''
    }art-collection/${transformedTitle}/${userId}/${
      isAASelection ? collection_id : data['id_user_collection']
    }/view`

    return addStoreLocaleByCookie(newCollectionPath)
  }

  const handleCreateCollectionOnSuccess = (
    payload: Record<string, any>,
    formData: Record<string, any>
  ) => {
    const newLocation = getCreatedCollectionUrl({ payload, formData, userId })

    window.location.assign(newLocation)
  }

  const handleOpenCreateCollectionsModal = () => {
    createCollectionsModalRef.current?.handleOpenModal()
  }

  /*
    Ensure that user doesn't redirect to Create URL until
    successfully logged in. If login fails, they don't leave this page.
   */
  const handleCreateCollection = (ev): void => {
    ev.preventDefault()

    if (!isAuthenticatedFlag) {
      showModal('login')
      createCollectionAfterLogin.current = true
      return
    }

    handleOpenCreateCollectionsModal()
  }

  const handleDeleteCollection = (ev): void => {
    ev.preventDefault()
    openDeleteModal()
  }

  useEffect(() => {
    if (!!createCollectionAfterLogin.current) {
      // Make a call to update the EVENT
      createCollectionAfterLogin.current = false
      handleOpenCreateCollectionsModal()
    }
  }, [isAuthenticatedFlag])

  const collectionDetailsActions = [
    {
      label: 'Edit',
      title: 'Edit Collection Details',
      href: adminUrlLinks.editCollectionUrl,
      onClick: (ev) => handleActionOnClick(ev, adminUrlLinks.editCollectionUrl),
      isHidden: !hasValidPermissions,
    },
    {
      label: 'Reorder',
      title: 'Reorder Images in Collection',
      href: adminUrlLinks.reorderUrl,
      onClick: (ev) => handleActionOnClick(ev, adminUrlLinks.reorderUrl),
      isHidden: !hasValidPermissions,
    },
    {
      label: 'Create a Collection',
      title: 'Create a New Collection',
      href: adminUrlLinks.collectionsUrl,
      onClick: (ev) => handleCreateCollection(ev),
      isHidden: !isAuthenticatedFlag,
    },
    {
      label: 'Duplicate',
      title: 'Create your own collection from this one',
      href: adminUrlLinks.duplicateCollectionUrl,
      onClick: (ev) => handleActionOnClick(ev, adminUrlLinks.duplicateCollectionUrl),
      isHidden: !isAuthenticatedFlag,
    },
    {
      label: 'Export CSV',
      title: 'Export Artist Emails from Collection',
      href: adminUrlLinks.collectionExportCSVUrl,
      onClick: (ev) => handleActionOnClick(ev, adminUrlLinks.collectionExportCSVUrl),
      isHidden: !isAdmin,
    },
    {
      label: 'Delete',
      title: 'Delete Collection',
      href: adminUrlLinks.collectionsUrl,
      onClick: (ev) => handleDeleteCollection(ev),
      isHidden: !hasValidPermissions,
    },
  ]

  return { collectionDetailsActions, handleCreateCollectionOnSuccess }
}

export default useCollectionDetailsActions
